import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as echarts from 'echarts';
// Use relative imports within the same library (libs/shared-lib-platform)
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

// Import utility functions for chart operations
import {
  formatValueLabel,
  formatPercentageLabel,
  formatNumberWithCommas,
  calculateCategoryTotals,
  convertToPercentage,
  sortSeriesByTotal,
  buildLegendSelectedState,
  applyOpacityToSeries,
  resetSeriesOpacity,
  buildConfigurableTooltipFormatter,
  buildConfigurableAxisLabel,
} from './../../utils/chart.utils';
import {
  getSeriesColor,
  getColorsForIndices,
  findIndicesByColors,
  ColorConfig,
} from './../../utils/color.utils';
import {
  extractGroupFromCategory as extractGroupUtil,
} from './../../utils/axis.utils';
import {
  matchValue,
  findIndicesByNames,
  buildLegendSyncData,
  buildCategoryFilterData,
  MatchMode,
} from './../../utils/filter.utils';

@Component({
  selector: 'generic-stackbar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './generic-stackbar-chart.html',
  styleUrl: './generic-stackbar-chart.scss',
})
export class GenericStackbarChart implements AfterViewInit, OnDestroy {
  @Input() data!: any; // widget config containing widgetId, apiUrl, title, etc.
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected reflowSub?: Subscription;
  protected myChart!: echarts.ECharts;
  protected allSeriesNames: string[] = []; // Store all series names for legend sync
  protected allCategories: string[] = []; // Store all category names for x-axis filtering
  protected isInternalLegendChange = false; // Flag to prevent infinite loop
  protected isCtrlPressed = false; // Track Ctrl key for multi-select
  protected selectedCategories: string[] = []; // Track selected categories (x-axis labels)
  protected barSeriesCount = 0; // Track number of bar series (categories) to exclude lines from filters
  protected enableQuarterYearAxis = false;
  protected xAxisQuarters: string[] = [];
  protected xAxisYears: string[] = [];
  protected xAxisYearsRaw: string[] = [];
  protected xAxisPrimaryLabels: string[] = [];
  protected xAxisSecondaryLabels: string[] = [];
  private filterSubscription!: Subscription; // track subscription to unsubscribe on destroy

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService,
  ) {
    this.filterSubscription = this.globalFilterService.filter$.subscribe((filters) => {
      if (!this.data?.widgetId) return;

      if (this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        if (!filters?.legendSync) {
          // Clear local selection state so highlights don't persist after reset.
          // Only clear legend selection for global filter changes (dropdowns, reset)
          // where widgetId is null. Chart-originated filter events (with widgetId)
          // may follow a legendSync that already set the correct selection state.
          this.selectedCategories = [];
          if (!filters?.widgetId) {
            this.currentlySelectedIndices = [];
          }
          this.loadAndRenderChart(filters);
          return;
        }

        // Legend sync logic
        if (filters?.legendSync && this.myChart) {
          if (filters.isCategoryFilter && filters.selectedLegends?.length > 0) {
            this.handleCategoryFilter(filters.selectedLegends, filters.sourceWidgetId);
          } else if (this.allSeriesNames.length > 0) {
            this.resetCategoryHighlight();

            const useColorSync = filters.colorSyncEnabled && filters.selectedColors?.length > 0;

            const isNameBasedFilter =
              !filters.isCategoryFilter &&
              filters.selectedLegends?.length > 0 &&
              (!filters.selectedIndices || filters.selectedIndices.length === 0);

            if (useColorSync) {
              this.syncLegendSelectionByColor(filters.selectedColors, filters.sourceWidgetId);
            } else if (isNameBasedFilter) {
              this.syncLegendSelectionByName(filters.selectedLegends, filters.sourceWidgetId);
            } else {
              this.syncLegendSelection(filters.selectedIndices || [], filters.sourceWidgetId);
            }
          } else if (
            filters.selectedLegends?.length === 0 &&
            filters.selectedIndices?.length === 0
          ) {
            this.resetCategoryHighlight();
            this.currentlySelectedIndices = [];
            this.selectLegendsByIndices([]);
          }

          // For category filters (label clicks), also trigger API reload
          if (filters.isCategoryFilter) {
            this.loadAndRenderChart(filters);
          }
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Wait for the div to render
    setTimeout(() => this.loadAndRenderChart(), 0);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Add keyboard listeners for Ctrl key (multi-select)
    this.addKeyboardListeners();
  }

  protected addKeyboardListeners(): void {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.handleWindowBlur);
  }

  protected handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Control') {
      this.isCtrlPressed = true;
    }
  };

  protected handleKeyUp = (event: KeyboardEvent): void => {
    if (event.key === 'Control') {
      this.isCtrlPressed = false;
    }
  };

  protected handleWindowBlur = (): void => {
    this.isCtrlPressed = false;
  };

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) {
      console.warn('Chart DOM not found:', this.data?.widgetId);
      return;
    }

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    // Determine whether to use API or inline data:
    // - If apiUrl is configured AND filters are present, prefer API so filter values are sent in the payload
    // - If only inline data (no apiUrl), use inline data for initial + filtered renders
    // - If no filters, inline data is fine for initial load
    const hasFilters = filters?.requestParams && Object.keys(filters.requestParams).length > 0;
    const useApi = this.data?.apiUrl && hasFilters;

    if (useApi) {
      // Fetch from API with filters in payload
      this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
        next: (res) => {
          const chartData = res?.data || res;
          this.initChart(chartData);
        },
        error: (err) => {
          console.error(`[GenericStackbar] Widget ${this.data?.widgetId} - API error:`, err);
          // Fallback to inline data if API fails and inline data exists
          if (this.data?.data) {
            this.initChart(this.data.data);
          } else {
            this.myChart.hideLoading();
          }
        },
      });
    } else if (this.data?.data) {
      // Static data provided in config (initial load or no apiUrl)
      this.initChart(this.data.data);
    } else if (this.data?.apiUrl) {
      // Fetch from API (initial load, no filters)
      this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
        next: (res) => {
          const chartData = res?.data || res;
          this.initChart(chartData);
        },
        error: (err) => {
          console.error(`[GenericStackbar] Widget ${this.data?.widgetId} - API error:`, err);
          this.myChart.hideLoading();
        },
      });
    } else {
      this.myChart.hideLoading();
      console.warn('No data or apiUrl provided for widget:', this.data?.widgetId);
    }

    this.addResizeListener();
  }

  protected initChart(chartData: any): void {
    if (!chartData) {
      console.warn('No chart data found for widget:', this.data?.widgetId);
      return;
    }

    // ===== EXTRACT CONFIGURATION WITH DEFAULTS (same as current behavior) =====
    const config = this.data?.chartConfig || {};
    const showLegend = config.showLegend !== false;
    const legendPosition = config.legendPosition || 'top';
    const showDataLabels = config.showDataLabels !== false;
    const dataLabelPosition = config.dataLabelPosition || 'inside';
    const showTotalLabels = config.showTotalLabels !== false;
    const colorMap = config.colorMap || {};
    const barWidth = config.barWidth;
    const showYAxisLabels = config.showYAxisLabels !== false;
    const stackType = config?.stackType || 'normal';
    this.enableQuarterYearAxis = config.enableQuarterYearAxis === true;
    const stackOrder = config?.stackOrder || 'desc'; // 'desc' = largest on top (default), 'asc' = smallest on top
    const seriesOrder: string[] = config?.seriesOrder || []; // Manual series order - if provided, overrides stackOrder

    // Position-based colors for cross-chart color consistency
    // If provided, colors are assigned by index (same index = same color across all charts)
    const positionColors = config.topNColorsByStackPosition || [];

    // ===== CONFIGURABLE FIELD NAMES (fully generic) =====
    const categoryFields: string[] = this.data?.categoryFields || ['quarter', 'year']; // Fields to combine for category
    const categoryDelimiter: string = this.data?.categoryDelimiter ?? ' '; // Delimiter between category fields
    const valueField: string = this.data?.valueField || 'value'; // Field containing the value

    // Line series configuration (optional)
    const lineSeriesDataKey = this.data?.lineSeriesDataKey;
    const lineSeriesKey = this.data?.lineSeriesKey;
    const lineSeriesValueField = this.data?.lineSeriesValueField || 'value'; // Field for line series values

    // Helper to build category string from configurable fields
    const buildCategoryString = (item: any): string => {
      return categoryFields.map((field) => item[field] ?? '').join(categoryDelimiter);
    };

    let categories: string[] = [];
    let seriesData: any[] = [];
    let lineSeriesData: any[] = []; // Store line series separately

    // 1. GENERIC FLAT ARRAY FORMAT (Configurable Keys)
    const dataKey = this.data?.dataKey;
    const seriesKey = this.data?.seriesKey;
    console.log(
      `[GenericStackbar] Widget ${this.data?.widgetId} - Format check: dataKey=${dataKey}, seriesKey=${seriesKey}, hasDataKey=${!!dataKey}, hasChartDataKey=${!!(dataKey && chartData[dataKey])}, isArray=${!!(dataKey && Array.isArray(chartData[dataKey]))}`,
    );

    if (dataKey && chartData[dataKey] && Array.isArray(chartData[dataKey])) {
      const flatData = chartData[dataKey];

      const uniqueCategories = Array.from(
        new Set(flatData.map((d: any) => buildCategoryString(d))),
      );
      categories = uniqueCategories;

      const uniqueSeriesNames = Array.from(
        new Set(flatData.map((d: any) => d[seriesKey] || 'Unknown')),
      );

      seriesData = uniqueSeriesNames.map((name: any) => {
        const seriesPoints = categories.map((cat) => {
          const point = flatData.find(
            (d: any) => d[seriesKey] === name && buildCategoryString(d) === cat,
          );

          if (point) {
            return {
              ...point,
              value: point[valueField] ?? 0,
            };
          }

          return { value: 0 };
        });

        return {
          name: name,
          type: 'bar',
          stack: 'total',
          emphasis: { focus: 'none' },
          data: seriesPoints,
        };
      });
    }

    // 2. BACKWARD COMPATIBILITY: Dictionary Format
    else if (!chartData.category && !chartData.series) {
      const seriesNames = Object.keys(chartData);
      if (seriesNames.length > 0) {
        const firstSeriesData = chartData[seriesNames[0]];
        if (Array.isArray(firstSeriesData)) {
          categories = firstSeriesData.map((item: any) => buildCategoryString(item));
        }
        seriesData = seriesNames.map((name) => {
          const rawData = chartData[name];
          return {
            name: name,
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'none' },
            data: Array.isArray(rawData)
              ? rawData.map((d: any) => ({
                ...d,
                value: d?.[valueField] ?? 0,
              }))
              : [],
          };
        });
      }
    }

    // 3. BACKWARD COMPATIBILITY: Standard Format (category + series)
    else if (chartData.category && chartData.series) {
      categories = chartData.category;
      seriesData = chartData.series.map((s: any) => ({
        name: s.name,
        type: 'bar',
        stack: 'total',
        emphasis: { focus: 'none' },
        data: s.data,
      }));
    }

    // 4. PARSE LINE SERIES DATA (Optional)
    // If lineSeriesDataKey is configured, parse line data separately
    console.log('[LineSeriesDebug] lineSeriesDataKey:', lineSeriesDataKey, 'chartData[lineSeriesDataKey]:', chartData[lineSeriesDataKey]);
    if (lineSeriesDataKey && chartData[lineSeriesDataKey] && Array.isArray(chartData[lineSeriesDataKey])) {
      const flatLineData = chartData[lineSeriesDataKey];
      console.log('[LineSeriesDebug] flatLineData length:', flatLineData.length, 'useSingleLineSeries:', this.data?.useSingleLineSeries);

      // Try to use existing categories, or extract from line data
      if (categories.length === 0) {
        const uniqueCategories = Array.from(
          new Set(flatLineData.map((d: any) => buildCategoryString(d))),
        );
        categories = uniqueCategories;
      }

      // Check if we should create a single line series or multiple grouped by lineSeriesKey
      const useSingleLineSeries = this.data?.useSingleLineSeries === true;

      if (useSingleLineSeries) {
        // Create a single line series taking first value per category
        const lineSeriesPoints = categories.map((cat) => {
          const point = flatLineData.find(
            (d: any) => buildCategoryString(d) === cat,
          );

          if (point) {
            return {
              ...point,
              value: point[lineSeriesValueField] ?? 0,
            };
          }

          return { value: 0 };
        });

        lineSeriesData = [
          {
            name: this.data?.lineSeriesName || 'Trend',
            type: 'line',
            smooth: true,
            yAxisIndex: 1,
            emphasis: { focus: 'none' },
            data: lineSeriesPoints,
          },
        ];
      } else {
        // Create multiple line series grouped by lineSeriesKey
        const uniqueLineSeriesNames = Array.from(
          new Set(flatLineData.map((d: any) => d[lineSeriesKey] || 'Unknown')),
        );

        lineSeriesData = uniqueLineSeriesNames.map((name: any) => {
          const seriesPoints = categories.map((cat) => {
            const point = flatLineData.find(
              (d: any) => d[lineSeriesKey] === name && buildCategoryString(d) === cat,
            );

            if (point) {
              return {
                ...point,
                value: point[lineSeriesValueField] ?? 0,
              };
            }

            return { value: 0 };
          });

          return {
            name: name,
            type: 'line',
            smooth: true,
            yAxisIndex: 1, // Use secondary Y-axis for line series
            emphasis: { focus: 'none' },
            data: seriesPoints,
          };
        });
      }
    }

    console.log('[LineSeriesDebug] Final lineSeriesData:', lineSeriesData);

    // Sort series based on configuration:
    // 1. If seriesOrder is provided, use that exact manual order (overrides stackOrder)
    // 2. Otherwise, sort by total value using stackOrder ('desc' = largest on top, 'asc' = smallest on top)
    let sortedSeriesData: any[];

    if (seriesOrder.length > 0) {
      // Manual order provided - sort series according to seriesOrder array
      // Series not in seriesOrder will be appended at the end in their original order
      sortedSeriesData = [...seriesData].sort((a, b) => {
        const indexA = seriesOrder.indexOf(a.name);
        const indexB = seriesOrder.indexOf(b.name);

        // Both in seriesOrder: sort by their position in the array
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        // Only A is in seriesOrder: A comes first
        if (indexA !== -1) return -1;
        // Only B is in seriesOrder: B comes first
        if (indexB !== -1) return 1;
        // Neither in seriesOrder: maintain original order
        return 0;
      });
    } else {
      // No manual order - use value-based sorting (existing functionality)
      sortedSeriesData = sortSeriesByTotal(seriesData, stackOrder === 'desc' ? 'asc' : 'desc');
    }

    // CRITICAL: Store bar series count and all series names for legend sync (DO NOT CHANGE)
    this.barSeriesCount = sortedSeriesData.length;
    this.allSeriesNames = sortedSeriesData.map((s) => s.name).concat(lineSeriesData.map((s) => s.name));

    // Store categories for x-axis click filtering (used for table cross-filtering)
    this.allCategories = categories;
    if (this.enableQuarterYearAxis) {
      this.xAxisQuarters = categories.map((cat: string) => {
        const qMatch = String(cat).match(/Q\s*([1-4])/i);
        return qMatch ? `Q${qMatch[1]}` : cat;
      });
      this.xAxisYearsRaw = categories.map((cat: string) => {
        const yMatch = String(cat).match(/(19|20)\d{2}/);
        return yMatch ? yMatch[0] : '';
      });
      this.xAxisYears = this.buildCenteredAxisLabels(this.xAxisYearsRaw);
    } else {
      this.xAxisQuarters = [];
      this.xAxisYears = [];
      this.xAxisYearsRaw = [];
    }

    // Calculate totals for each category using utility
    const totals = calculateCategoryTotals(categories, seriesData);

    // Apply percentage conversion if stackType is 'percentage' using utility
    const displaySeriesData =
      stackType === 'percentage' ? convertToPercentage(sortedSeriesData, totals) : sortedSeriesData;

    // Build color config for utilities
    const colorConfig: ColorConfig = {
      colorMap,
      topNColorsByStackPosition: positionColors,
    };

    // Build series with labels and colors using utility for color retrieval
    const seriesWithLabels = displaySeriesData.map((s, index) => {
      // Get color using utility (position-based first, then name-based)
      let seriesColor = getSeriesColor(index, s.name, colorConfig);

      if (this.data?.enableColorConsistency) {
        const globalColor = this.chartFilterHandler.getItemColor(s.name);
        if (globalColor) {
          seriesColor = globalColor;
        } else if (seriesColor) {
          this.chartFilterHandler.registerItemColor(s.name, seriesColor);
        }
      }

      const seriesConfig: any = {
        ...s,
        ...(barWidth ? { barMaxWidth: barWidth } : {}),
        ...(seriesColor ? { itemStyle: { color: seriesColor } } : {}),
      };

      if (showDataLabels) {
        seriesConfig.label = {
          show: true,
          position: dataLabelPosition,
          formatter: (params: any) =>
            stackType === 'percentage'
              ? formatPercentageLabel(params.value)
              : formatValueLabel(params.value),
          fontSize: 10,
          color: dataLabelPosition === 'inside' ? '#fff' : '#333',
        };
      }

      return seriesConfig;
    });

    // Build final series array
    const allSeries: any[] = [...seriesWithLabels];

    // Add line series if configured
    if (lineSeriesData.length > 0) {
      const lineSeriesWithLabels = lineSeriesData.map((s, index) => {
        // Get color for line series (position-based or name-based)
        const seriesColor = getSeriesColor(index, s.name, colorConfig);

        const seriesConfig: any = {
          ...s,
          smooth: true,
          yAxisIndex: 1,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2,
            ...(seriesColor ? { color: seriesColor } : {}),
          },
          ...(seriesColor ? { itemStyle: { color: seriesColor } } : {}),
        };

        if (showDataLabels) {
          seriesConfig.label = {
            show: true,
            position: 'top',
            formatter: (params: any) => formatValueLabel(params.value),
            fontSize: 10,
            color: '#333',
          };
        }

        return seriesConfig;
      });

      // Add line series to the array (after bars, before Total)
      allSeries.push(...lineSeriesWithLabels);
    }

    // Add total series if enabled
    if (showTotalLabels) {
      allSeries.push({
        name: 'Total',
        type: 'bar',
        stack: 'total',
        data: totals.map(() => 0),
        label: {
          show: true,
          position: 'top',
          distance: 8,
          formatter: (params: any) =>
            stackType === 'percentage' ? '100%' : formatValueLabel(totals[params.dataIndex]),
          fontSize: 11,
          fontWeight: 'bold',
          color: '#ffffff',
        },
        itemStyle: { color: 'transparent' },
        emphasis: { itemStyle: { color: 'transparent' } },
      });
    }

    // Build legend config
    const allNamesForLegend = [
      ...sortedSeriesData.map((s) => s.name),
      ...lineSeriesData.map((s) => s.name),
    ];

    const legendConfig: any = {
      show: showLegend,
      data: allNamesForLegend.reverse(),
      type: 'scroll',
      itemGap: 16,
      padding: [5, 10, 10, 10],
    };
    if (legendPosition === 'top') legendConfig.top = 0;
    else if (legendPosition === 'bottom') legendConfig.bottom = 0;
    else if (legendPosition === 'left') {
      legendConfig.left = 0;
      legendConfig.orient = 'vertical';
    } else if (legendPosition === 'right') {
      legendConfig.right = 0;
      legendConfig.orient = 'vertical';
    }

    // Build chart option
    const yearAxisOffset = Number(config.yearAxisOffset ?? 22);
    const xAxisConfig = this.enableQuarterYearAxis
      ? ([
        {
          type: 'category',
          data: this.xAxisQuarters,
          triggerEvent: true,
          axisLabel: { clickable: true },
          axisTick: { alignWithLabel: true },
        },
        {
          type: 'category',
          data: this.xAxisYears,
          position: 'bottom',
          offset: yearAxisOffset,
          triggerEvent: true,
          axisLabel: {
            clickable: true,
            interval: 0,
          },
          axisTick: { show: false },
          axisLine: { show: true },
        },
      ] as any)
      : ({
        type: 'category',
        data: categories,
        triggerEvent: true,
        axisLabel: { clickable: true },
      } as any);

    const option: any = {
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        padding: [10, 14],
        textStyle: { color: '#333', fontSize: 13 },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          const configurableFormatter = buildConfigurableTooltipFormatter(tooltipCfg);
          if (configurableFormatter) return configurableFormatter;

          return (params: any) => {
            if (params.seriesName === 'Total') return '';
            const categoryIndex = params.dataIndex;
            const total = totals[categoryIndex];

            if (stackType === 'percentage') {
              const originalSeries = seriesData.find((s) => s.name === params.seriesName);
              const originalValue = originalSeries?.data[categoryIndex] || 0;
              return `
                <div style="line-height: 1.8;">
                  <div><b>Year Quarter:</b> ${params.name}</div>
                  <div><b>Updated Segment:</b> ${params.seriesName}</div>
                  <div><b>Percentage:</b> ${params.value.toFixed(1)}%</div>
                  <div><b>Value:</b> ${formatNumberWithCommas(originalValue)}</div>
                  <div><b>Total:</b> ${formatNumberWithCommas(total)}</div>
                </div>
              `;
            }

            return `
              <div style="line-height: 1.8;">
                <div><b>Year Quarter:</b> ${params.name}</div>
                <div><b>Updated Segment:</b> ${params.seriesName}</div>
                <div><b>Sum of Total Sales:</b> ${formatNumberWithCommas(params.value)}</div>
                <div><b>Total:</b> ${formatNumberWithCommas(total)}</div>
              </div>
            `;
          };
        })(),
      },
      legend: legendConfig,
      grid: {
        left: '3%',
        right: lineSeriesData.length > 0 ? '8%' : '4%', // Add more right margin for secondary Y-axis
        top: legendPosition === 'top' && showLegend ? '12%' : '5%',
        bottom: this.enableQuarterYearAxis ? (config.gridBottom ?? 0) : (config.gridBottom ?? 0),
        containLabel: true,
      },
      xAxis: xAxisConfig,
      yAxis: lineSeriesData.length > 0
        ? [
          {
            type: 'value',
            name: 'Sales',
            position: 'left',
            splitLine: {
              show: false,
            },
            axisLabel: {
              show: showYAxisLabels,
              formatter: stackType === 'percentage' ? '{value}%' : undefined,
            },
            ...(stackType === 'percentage' ? { max: 100 } : {}),
          },
          {
            type: 'value',
            name: 'Gross Margin %',
            position: 'right',
            splitLine: {
              show: false,
            },
            axisLabel: {
              show: true,
              formatter: '{value}%',
            },
            min: config.rightAxisMin ?? 40,
            max: 100,
          },
        ]
        : {
          type: 'value',
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: showYAxisLabels,
            formatter: stackType === 'percentage' ? '{value}%' : undefined,
          },
          ...(stackType === 'percentage' ? { max: 100 } : {}),
        },
      series: allSeries,
    };

    if (this.myChart) {
      this.currentlySelectedIndices = [];
      this.myChart.setOption(option, true);
      this.myChart.hideLoading();
      // Re-apply any active visual selection so state survives a data reload
      if (this.currentlySelectedIndices.length > 0) {
        this.selectLegendsByIndices(this.currentlySelectedIndices);
      } else if (this.selectedCategories.length > 0) {
        this.highlightCategories(this.selectedCategories);
      }
      this.setupChartClickEvent();
      this.setupLegendClickEvent();
    }
  }

  protected setupChartClickEvent(): void {
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      // Legend clicks are handled exclusively by `legendselectchanged`.
      if (params?.componentType === 'legend') {
        const nativeEvent = params?.event?.event;
        nativeEvent?.preventDefault?.();
        nativeEvent?.stopPropagation?.();
        return;
      }

      // Ignore non-data clicks to avoid accidental filter payloads.
      if (
        params?.componentType !== 'series' &&
        params?.componentType !== 'xAxis'
      ) {
        return;
      }

      // Ignore non-datapoint series events (legend interaction can surface here).
      if (
        params?.componentType === 'series' &&
        !Number.isInteger(params?.dataIndex)
      ) {
        return;
      }

      if (
        params?.componentType === 'xAxis' &&
        (this.data?.chartConfig?.syncXaxisLabels ?? true)
      ) {
        this.handleXAxisLabelClick(params);
        return;
      }

      console.log('2334 params 1', params);
      this.updateFilterSelection(params);
    });
  }

  protected setupLegendClickEvent(): void {
    this.myChart.off('legendselectchanged');
    this.myChart.on('legendselectchanged', (params: any) => {
      const nativeEvent = params?.event?.event;
      nativeEvent?.preventDefault?.();
      nativeEvent?.stopPropagation?.();
      console.log('2334 params 2', params);
      this.updateLegendFilterSelection(params);
    });
  }

  protected currentlySelectedIndices: number[] = []; // Track current selection for toggle

  private extractYearFromCategory(cat: string): string {
    const m = String(cat).match(/(19|20)\d{2}/);
    return m ? m[0] : '';
  }

  protected updateFilterSelection(params: any): void {
    if (!this.data?.filterKey) return;

    // Normalize filterKey to array
    const filterKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : this.data.filterKey.split(',').map((k: string) => k.trim());

    const payload: any = {};

    filterKeys.forEach((key: string) => {
      // 1. Try to get value directly from data object if it exists
      if (params.data && typeof params.data === 'object' && key in params.data) {
        payload[key] = params.data[key];
        return;
      }

      console.log('payload', payload);

      // 2. Intelligent mapping based on config
      // Map configured seriesKey (e.g. "segment") to params.seriesName
      if (key === this.data.seriesKey) {
        payload[key] = params.seriesName;
        return;
      }

      // Map configured xAxis/categoryFields (e.g. "year") to params.name
      if (
        key === this.data.xAxis ||
        (this.data.categoryFields && this.data.categoryFields.includes(key))
      ) {
        payload[key] = params.name;
        return;
      }

      // 3. Fallbacks (but strictly typed)
      if (key === 'series' || key === 'segment') {
        payload[key] = params.seriesName;
        return;
      }

      if (key === 'year' || key === 'quarter' || key === 'qtr' || key === 'category') {
        payload[key] = params.name;
        return;
      }
    });

    if (Object.keys(payload).length === 0) return;

    console.log('[GenericStackbar] Constructed Payload:', payload);
    this.chartFilterHandler.updateFilterSelection(this.data, payload);
  }

  protected handleXAxisLabelClick(params: any): void {
    if (!this.data?.chartConfig?.enableQuarterYearAxis) {
      const clickedCategory = params.value;
      this.applyCategorySelection(clickedCategory, params);
      return;
    }

    const axisIndex = params?.axisIndex;
    const idx = params?.valueIndex ?? params?.dataIndex;
    if (idx == null || idx < 0) return;
    const rawValue = String(params?.value ?? '').trim();
    const isYearValue = /^(19|20)\d{2}$/.test(rawValue);
    const isQuarterValue = /^Q\s*[1-4]$/i.test(rawValue);

    if (axisIndex === 0 || (axisIndex == null && isQuarterValue)) {
      const fullCategory = this.allCategories[idx];
      this.applyCategorySelection(fullCategory, params);
      return;
    }

    if (axisIndex === 1 || (axisIndex == null && isYearValue)) {
      const clickedYear = this.xAxisYearsRaw[idx] || this.xAxisYears[idx] || rawValue;
      if (!clickedYear) return;

      const nativeEvent = params.event?.event || params.event;
      const ctrlPressed = this.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;

      const selectedYears = Array.from(
        new Set(
          (this.selectedCategories || [])
            .map((c: string) => this.extractYearFromCategory(c))
            .filter(Boolean),
        ),
      );

      let nextYears: string[];
      if (ctrlPressed) {
        nextYears = selectedYears.includes(clickedYear)
          ? selectedYears.filter((y) => y !== clickedYear)
          : [...selectedYears, clickedYear];
      } else {
        nextYears =
          selectedYears.length === 1 && selectedYears[0] === clickedYear ? [] : [clickedYear];
      }

      const nextCategories =
        nextYears.length === 0
          ? []
          : this.allCategories.filter((c: string) =>
            nextYears.includes(this.extractYearFromCategory(c)),
          );

      this.currentlySelectedIndices = [];
      this.selectLegendsByIndices([]);

      if (nextCategories.length === 0) {
        this.selectedCategories = [];
        this.resetCategoryHighlight();
        this.updateXAxisFiltersFromSelectedCategories();
        this.broadcastCategoryFilter([]);
      } else {
        this.selectedCategories = nextCategories;
        this.highlightCategories(nextCategories);
        this.updateXAxisFiltersFromSelectedCategories();
        this.broadcastCategoryFilter(nextCategories);
      }
      return;
    }
  }

  private applyCategorySelection(clickedCategory: any, params: any): void {
    if (!clickedCategory) return;

    if (this.data?.isMultiSelect) {
      const pos = this.selectedCategories.indexOf(clickedCategory);
      this.selectedCategories =
        pos === -1
          ? [...this.selectedCategories, clickedCategory]
          : this.selectedCategories.filter((c) => c !== clickedCategory);
    } else {
      if (this.selectedCategories.length === 1 && this.selectedCategories[0] === clickedCategory) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = [clickedCategory];
      }
    }

    this.currentlySelectedIndices = [];
    this.selectLegendsByIndices([]);

    if (this.selectedCategories.length === 0) {
      this.resetCategoryHighlight();
      this.updateXAxisFiltersFromSelectedCategories();
      this.broadcastCategoryFilter([]);
    } else {
      this.highlightCategories(this.selectedCategories);
      this.updateXAxisFiltersFromSelectedCategories();
      this.broadcastCategoryFilter(this.selectedCategories);
    }
  }

  private extractGroupFromCategory(cat: string): string {
    return extractGroupUtil(cat, this.data?.chartConfig?.xAxis2GroupRegex);
  }

  private buildSecondaryAxisGroups(categories: string[]): {
    labels: string[];
    tickPositions: number[];
  } {
    const groupValues = categories.map((cat) => this.extractGroupFromCategory(cat));
    const groups: { value: string; start: number; end: number }[] = [];
    let currentGroup = '';
    for (let i = 0; i < groupValues.length; i++) {
      if (groupValues[i] !== currentGroup) {
        if (groups.length > 0) {
          groups[groups.length - 1].end = i - 1;
        }
        currentGroup = groupValues[i];
        groups.push({ value: currentGroup, start: i, end: i });
      } else {
        groups[groups.length - 1].end = i;
      }
    }

    const labels: string[] = new Array(categories.length).fill('');
    const tickPositions: number[] = [];

    groups.forEach((group, idx) => {
      const centerIdx = Math.floor((group.start + group.end) / 2);
      labels[centerIdx] = group.value;
      if (idx > 0) {
        tickPositions.push(group.start);
      }
    });

    return { labels, tickPositions };
  }

  private resolveListenerConfig(...candidates: any[]): any {
    for (const c of candidates) {
      if (c && (!Array.isArray(c) || c.length > 0)) return c;
    }
    return [];
  }

  protected handleYAxis2Click(params: any): void {
    const clickedValue = params.value;
    if (!clickedValue) return;

    const listenerConfig = this.resolveListenerConfig(
      this.data?.yAxis2ClickListener,
      this.data?.labelListener,
      this.data?.labelListner,
      this.data?.filterListener,
      this.data?.filterListner,
    );

    const filterData = buildCategoryFilterData([String(clickedValue)], this.data.widgetId, listenerConfig, this.data?.labelKey || this.data?.legendKey || this.data?.filterKey);
    (this.globalFilterService as any).filterSubject.next(filterData);
  }

  private extractQuarterFromCategory(cat: string): string {
    const str = String(cat ?? '').toUpperCase();
    const qMatch = str.match(/\bQ\s*([1-4])\b/);
    if (qMatch) return `Q${qMatch[1]}`;
    return '';
  }

  protected buildCenteredAxisLabels(values: string[]): string[] {
    const centered = new Array(values.length).fill('');
    for (let start = 0; start < values.length;) {
      const value = values[start];
      let end = start;
      while (end + 1 < values.length && values[end + 1] === value) {
        end++;
      }
      if (value) {
        const midpoint = Math.floor((start + end) / 2);
        centered[midpoint] = value;
      }
      start = end + 1;
    }
    return centered;
  }

  protected updateXAxisFiltersFromSelectedCategories(): void {
    const enableQtr = !!this.data?.chartConfig?.enableQuarterYearAxis;
    const listener = this.data?.filterListener || this.data?.filterListner;
    const seriesKey = this.data?.seriesKey || 'segment';
    const legendSeriesValues = this.currentlySelectedIndices
      .map((i: number) => this.allSeriesNames[i])
      .filter(Boolean);

    if (!this.selectedCategories?.length) {
      const keysToClear = enableQtr ? ['year', 'qtr'] : ['year'];
      keysToClear.forEach((key, index) => {
        const isLast = index === keysToClear.length - 1;
        if (isLast) {
          this.globalFilterService.resetFilterSelection(key, false, listener);
        } else {
          this.globalFilterService.deleteKey(key, false);
        }
      });
      return;
    }

    const yearValues = this.selectedCategories
      .map((cat: string) => this.extractYearFromCategory(cat))
      .filter(Boolean);

    if (!yearValues.length) return;

    if (!enableQtr) {
      const payload: any = { year: yearValues.length === 1 ? yearValues[0] : yearValues };
      const cfg: any = { ...this.data, filterKey: ['year'] };
      if (legendSeriesValues.length > 0) {
        payload[seriesKey] = legendSeriesValues.length === 1 ? legendSeriesValues[0] : legendSeriesValues;
        cfg.filterKey = [seriesKey, 'year'];
      }
      this.chartFilterHandler.updateFilterSelection(cfg, payload);
      return;
    }

    const qtrValues = this.selectedCategories
      .map((cat: string) => this.extractQuarterFromCategory(cat))
      .filter(Boolean);

    if (!qtrValues.length) return;

    const payload: any = {
      year: yearValues.length === 1 ? yearValues[0] : yearValues,
      qtr: qtrValues.length === 1 ? qtrValues[0] : qtrValues,
    };

    const cfg: any = { ...this.data, filterKey: ['year', 'qtr'] };
    if (legendSeriesValues.length > 0) {
      payload[seriesKey] = legendSeriesValues.length === 1 ? legendSeriesValues[0] : legendSeriesValues;
      cfg.filterKey = [seriesKey, 'year', 'qtr'];
    }
    this.chartFilterHandler.updateFilterSelection(cfg, payload);
  }

  protected broadcastCategoryFilter(categories: string[], isSecondaryAxisClick = false): void {
    const listenerConfig = isSecondaryAxisClick
      ? this.resolveListenerConfig(
        this.data?.xAxis2ClickListener,
        this.data?.labelListener,
        this.data?.labelListner,
        this.data?.filterListener,
        this.data?.filterListner,
      )
      : this.resolveListenerConfig(
        this.data?.labelListener,
        this.data?.labelListner,
        this.data?.filterListener,
        this.data?.filterListner,
      );

    const labelKey = this.data?.labelKey || this.data?.filterKey;
    if (labelKey) {
      if (categories.length > 0) {
        this.globalFilterService.updateFilterSelection(labelKey, categories, false, this.data.widgetId, false, listenerConfig);
      } else {
        this.globalFilterService.deleteKey(labelKey, false);
      }
    }

    if (isSecondaryAxisClick && this.data?.doublexAxisKey) {
      if (categories.length > 0) {
        const groupValues = Array.from(new Set(categories.map((c: string) => this.extractGroupFromCategory(c)).filter(Boolean)));
        if (groupValues.length > 0) {
          this.globalFilterService.updateFilterSelection(this.data.doublexAxisKey, groupValues, false, this.data.widgetId, false, listenerConfig);
        }
      } else {
        this.globalFilterService.deleteKey(this.data.doublexAxisKey, false);
      }
    }

    const filterData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(categories, this.data.widgetId, listenerConfig, this.data?.labelKey || this.data?.legendKey || this.data?.filterKey),
    };
    (this.globalFilterService as any).filterSubject.next(filterData);
  }

  protected updateLegendFilterSelection(params: any): void {
    if (this.isInternalLegendChange) return;

    const nativeEvent = params?.event?.event;
    nativeEvent?.preventDefault?.();
    nativeEvent?.stopPropagation?.();

    const clickedLegend = params.name;
    if (clickedLegend === 'Total') return;

    const clickedIndex = this.allSeriesNames.indexOf(clickedLegend);
    if (clickedIndex === -1) return;

    const ctrlPressed = nativeEvent ? !!(nativeEvent.ctrlKey || nativeEvent.metaKey) : this.isCtrlPressed;

    if (ctrlPressed) {
      this.isInternalLegendChange = true;
      if (this.data?.isMultiSelect) {
        const pos = this.currentlySelectedIndices.indexOf(clickedIndex);
        if (pos === -1) {
          this.currentlySelectedIndices = [...this.currentlySelectedIndices, clickedIndex];
        } else {
          this.currentlySelectedIndices = this.currentlySelectedIndices.filter((i) => i !== clickedIndex);
        }
      } else {
        if (this.currentlySelectedIndices.length === 1 && this.currentlySelectedIndices[0] === clickedIndex) {
          this.currentlySelectedIndices = [];
        } else {
          this.currentlySelectedIndices = [clickedIndex];
        }
      }

      const legendListener = this.data?.legendListener || this.data?.legendListner;
      const legendFilterKey = this.data?.legendKey || this.data?.legendFilterKey || this.data?.legendFilterkey;
      const cloned = legendListener ? { ...this.data, filterListener: legendListener, ...(legendFilterKey ? { filterKey: legendFilterKey } : {}) } : this.data;

      if (this.currentlySelectedIndices.length === 0) {
        this.selectLegendsByIndices([]);
        this.broadcastLegendSync([]);
        try { this.chartFilterHandler.updateFilterSelection(cloned, []); } catch (e) { }
      } else {
        this.selectLegendsByIndices(this.currentlySelectedIndices);
        this.broadcastLegendSync(this.currentlySelectedIndices);
        try {
          const names = this.currentlySelectedIndices
            .filter((i) => i < this.barSeriesCount)
            .map((i) => this.allSeriesNames[i]);
          this.chartFilterHandler.updateFilterSelection(cloned, names.length === 1 ? names[0] : names);
        } catch (e) { }
      }
      this.isInternalLegendChange = false;
      return;
    }

    this.currentlySelectedIndices = [clickedIndex];
    this.selectLegendsByIndices([clickedIndex]);
    this.broadcastLegendSync([clickedIndex]);

    try {
      const legendListener = this.data?.legendListener || this.data?.legendListner;
      const legendFilterKey = this.data?.legendFilterKey || this.data?.legendFilterkey;
      const cloned = legendListener ? { ...this.data, filterListener: legendListener, ...(legendFilterKey ? { filterKey: legendFilterKey } : {}) } : this.data;
      const name = clickedIndex < this.barSeriesCount ? this.allSeriesNames[clickedIndex] : [];
      this.chartFilterHandler.updateFilterSelection(cloned, name);
    } catch (e) { }

    this.isInternalLegendChange = false;
  }

  protected broadcastLegendSync(selectedIndices: number[]): void {
    if (!this.data.widgetId) return;
    const categoryOnlyIndices = selectedIndices.filter((i) => i < this.barSeriesCount);
    const listenerConfig = this.data?.legendListener || this.data?.legendListner || this.data?.filterListener || this.data?.filterListner;
    const selectedColors = this.getColorsForSelectedIndices(selectedIndices);
    const colorSyncEnabled = this.data?.chartConfig?.colorSyncEnabled === true;
    const legendKey = this.data?.legendKey || this.data?.legendFilterKey || this.data?.legendFilterkey || this.data?.filterKey;

    const filterData = buildLegendSyncData(categoryOnlyIndices, this.allSeriesNames, this.data.widgetId, listenerConfig, { colorSyncEnabled, selectedColors, legendKey });

    if (filterData.selectedLegends && this.data?.lineSeriesName) {
      const lineName = this.data.lineSeriesName.toLowerCase().trim();
      filterData.selectedLegends = filterData.selectedLegends.filter((n) => n.toLowerCase().trim() !== lineName);
    }

    (this.globalFilterService as any).filterSubject.next(filterData);
  }

  protected getColorsForSelectedIndices(indices: number[]): string[] {
    const config = this.data?.chartConfig || {};
    const colorConfig: ColorConfig = { colorMap: config.colorMap || {}, topNColorsByStackPosition: config.topNColorsByStackPosition || [] };
    return getColorsForIndices(indices, this.allSeriesNames, colorConfig);
  }

  protected syncLegendSelection(selectedIndices: number[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data.widgetId) return;
    if (!this.myChart || !this.allSeriesNames.length) return;
    const indices = [...selectedIndices];
    if (this.data?.lineSeriesName) {
      const lineIndex = this.allSeriesNames.indexOf(this.data.lineSeriesName);
      if (lineIndex !== -1 && !indices.includes(lineIndex)) indices.push(lineIndex);
    }
    this.currentlySelectedIndices = indices;
    this.selectLegendsByIndices(indices);
  }

  protected syncLegendSelectionByColor(selectedColors: string[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data.widgetId) return;
    if (!this.myChart || !this.allSeriesNames.length) return;
    const matchingIndices = this.findSeriesIndicesByColors(selectedColors);
    if (matchingIndices.length === 0) return;
    this.currentlySelectedIndices = matchingIndices;
    this.selectLegendsByIndices(matchingIndices);
  }

  protected findSeriesIndicesByColors(colors: string[]): number[] {
    const config = this.data?.chartConfig || {};
    const colorConfig: ColorConfig = { colorMap: config.colorMap || {}, topNColorsByStackPosition: config.topNColorsByStackPosition || [] };
    const indices = findIndicesByColors(colors, this.allSeriesNames, colorConfig);
    if (this.data?.lineSeriesName) {
      const lineIndex = this.allSeriesNames.indexOf(this.data.lineSeriesName);
      if (lineIndex !== -1 && !indices.includes(lineIndex)) indices.push(lineIndex);
    }
    return indices;
  }

  protected syncLegendSelectionByName(selectedLegends: string[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data.widgetId) return;
    if (!this.myChart || !this.allSeriesNames.length) return;
    const matchingIndices = this.findSeriesIndicesByNames(selectedLegends);
    if (matchingIndices.length === 0) return;
    this.currentlySelectedIndices = matchingIndices;
    this.selectLegendsByIndices(matchingIndices);
  }

  protected findSeriesIndicesByNames(legendNames: string[]): number[] {
    const indices = findIndicesByNames(this.allSeriesNames, legendNames);
    if (this.data?.lineSeriesName) {
      const lineIndex = this.allSeriesNames.indexOf(this.data.lineSeriesName);
      if (lineIndex !== -1 && !indices.includes(lineIndex)) indices.push(lineIndex);
    }
    return indices;
  }

  protected selectLegendsByIndices(selectedIndices: number[]): void {
    if (!this.myChart) return;
    this.isInternalLegendChange = true;
    const selected = buildLegendSelectedState(this.allSeriesNames, selectedIndices);
    if (this.data?.lineSeriesName) {
      const lineIndex = this.allSeriesNames.indexOf(this.data.lineSeriesName);
      if (selectedIndices.length === 0 || (lineIndex !== -1 && !selectedIndices.includes(lineIndex))) {
        selected[this.data.lineSeriesName] = true;
      }
    }
    this.myChart.setOption({ legend: { selected: selected } }, { lazyUpdate: false });
    this.isInternalLegendChange = false;
  }

  protected handleCategoryFilter(selectedLegends: string[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data.widgetId) return;
    if (!this.myChart || !this.allCategories.length) return;
    if (selectedLegends.length === 0) { this.resetCategoryHighlight(); return; }
    const matchingCategories = this.allCategories.filter((cat: string) => selectedLegends.some((legend: string) => this.matchCategory(cat, legend)));
    if (matchingCategories.length > 0) {
      this.selectedCategories = matchingCategories;
      this.highlightCategories(matchingCategories);
    }
  }

  protected matchCategory(value: string, searchValue: string): boolean {
    const mode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
    return matchValue(value, searchValue, mode);
  }

  protected highlightCategories(categories: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option || !option.series) return;
    const categoryIndices = categories.map((cat: string) => this.allCategories.indexOf(cat)).filter((i: number) => i !== -1);
    const updatedSeries = applyOpacityToSeries(option.series, categoryIndices, 1, 0.4);
    this.myChart.setOption({ series: updatedSeries }, { lazyUpdate: false });
  }

  protected resetCategoryHighlight(): void {
    this.selectedCategories = [];
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option || !option.series) return;
    const updatedSeries = resetSeriesOpacity(option.series);
    this.myChart.setOption({ series: updatedSeries }, { lazyUpdate: false });
  }

  protected resizeObserver: ResizeObserver | null = null;

  protected addResizeListener(): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (chartDom) {
      this.resizeObserver = new ResizeObserver(() => { this.myChart?.resize(); });
      this.resizeObserver.observe(chartDom);
    }
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => { this.myChart?.resize(); };

  public reset(): void {
    this.selectedCategories = [];
    if (this.myChart) {
      this.resetCategoryHighlight();
      this.selectLegendsByIndices([]);
    }
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.reflowSub?.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleWindowBlur);
    this.myChart?.dispose();
  }
}
